//
//	+-----------------------------------------------+
//	| Cardboard data collection objects             |
// 	+-----------------------------------------------+

/**
 * @file cardboard/collections.js
 * @desc This contains model objects that encapsulate the results of the various
 * requests made to the WebFOCUS and/or JSON backends.
 * @author Geoffrey Pursell
 */

define('cardboard/collections',["jquery", "underscore", "backbone", "backbone-documentmodel", "rsvp",
	    "cardboard/util", "cardboard/models", "cardboard/mixins" ],
function ($, _, Backbone, BackboneDocumentModel, RSVP,
		 Util, CardboardModels, Mixins ) {

	console.log("MODULE: cardboard/collections");

	/**
	 * @exports cardboard/collections
	 * @desc Cardboard model objects: Contains the model objects that describe
	 * underlying data sources in cardboard
	 */
	var CardboardCollections = {},
		Promise = RSVP.Promise;

	//------------------------------------------------------------
	// === Data Collections									     |
	//------------------------------------------------------------

	/**
	 * @class
	 * @classdesc Collection collection: this is our lightly
	 * customized foundation of all data requests
	 * @augments Backbone.Collection
	 */
	CardboardCollections.Collection = Backbone.Collection.extend( /** @lends module:cardboard/models.Collection.prototype */ {

		// METHODS

		/** constructor
		 * @param {Array} models an array of objects to pre-load into the collection
		 * @param {Object} options the options hash that sets up the collection */
		initialize: function (models, options) {
			var setup = _({}).extend(options).value();

			this.dataSourceInfo = setup;
			this.loaded = false;

			// save bits of the setup we were passed
			if (setup.url) { this.url = options.url; }

			if (setup.defaultParams) { this.setParams(setup.defaultParams || {}); }
		 	this.params = {
                    COHORTID: "2021"
                }; 
			this.catchErrors();
		},

		/** wrap original fetch to return a Promise instead of a Deferred */
		fetch: function () {
			var deferred = Backbone.Collection.prototype.fetch.apply(this, arguments);
			return Promise.cast(deferred);
		},

		parse: function (/*input*/) {
			var data = Backbone.Collection.prototype.parse.apply(this, arguments);
			this.postProcess(data);
			this.loaded = true;
			return data;
		},

		// INFO methods

		/**
		 * @param {String} fieldName the name of a field to look for
		 * @returns {boolean} Whether a field is present in a row of
		 * this collection */
		hasField: function (fieldName) { return this.at(0).has(fieldName); },

		/**
		 * @returns {Array} List of fields in each row of this
		 * collection */
		getFieldNames: function () { return _.keys(this.at(0).attributes); },

		/**
		 * @returns {Boolean} Whether the data has been loaded yet or
		 * not */
		isLoaded: function () { return (this.length > 0); },

		/**
		 * @param {String} yearFieldPath the name of a field in which to look for the year value
		 * @returns {String} a string that describes the fiscal years
		 * this data is valid for */
		getYearsArray: function (yearFieldPath) {
			var yearsArray = _(this.pluck(yearFieldPath))
					.uniq()
					.sortBy(function (year) {
						return year;
					}).value();
			return yearsArray;
		},

		getYearString: function (yearFieldPath) {
			if (!yearFieldPath) { yearFieldPath = this.getDefinition().yearPath; }

			var yearsArray = this.getYearsArray(yearFieldPath);

			// if it's a sequnce, we show an abbreviated version
			if (Util.Data.isSequence(yearsArray)) {
				return _(yearsArray).first() + " - " + _(yearsArray).last();
			}

			return yearsArray.join(", ");
		},
		getLastYearString: function (lastYearFieldPath) {
			if (!lastYearFieldPath) { lastYearFieldPath = this.getDefinition().lastYearPath; }

			if (lastYearFieldPath) {
				// okay, we've got a real last year path, either from the param
				// or set on the data source info
				var yearsArray = this.getYearsArray(lastYearFieldPath);

				// if it's a sequnce, we show an abbreviated version
				if (Util.Data.isSequence(yearsArray)) {
					return _(yearsArray).first() + " - " + _(yearsArray).last();
				}
				return yearsArray.join(", ");
			} else {
				// there' no last year available -- let's calculate one, if we can.
				var year = parseInt(this.getYearString(), 10);
				return (year - 1) + "";
			}

		}
	});

	_.extend(CardboardCollections.Collection.prototype, Mixins.ModelAndCollectionMixin);

	/**
	 * @class
	 * @classdesc Base DocumentCollection class; we need this for detailed report
	 * JSON documents that we want to automatically turn their nested arrays
	 * into proper collections.
	 * @augments Backbone.DocumentCollection
	 * @mixes Models.ModelAndCollectionMixin 
	 */
	CardboardCollections.DocumentCollection = Backbone.DocumentCollection.extend(/** @lends module:cardboard/models.DocumentCollection.prototype */ {
	});

	_.extend(CardboardCollections.DocumentCollection.prototype, Mixins.ModelAndCollectionMixin);

	/**
	 * @class
	 * @classdesc DerivativeCollectionData: for the generic collection data, which needs
	 * to be re-filtered any time a new report is selected and put into its
	 * own (simple!) collection for its view to watch
	 * @augments Collections.Collection
	 */
	CardboardCollections.DerivativeCollectionData = CardboardCollections.Collection.extend({ /** @lends module:cardboard/models.DerivativeCollectionData.prototype */
		// nothing to do here! it's been fetched already!
		fetch: function () {
			
			this.trigger("sync", this);
			return null;
		}
	});

	/**
	 * @class
	 * @classdesc DemoCatProducer class; make little tweaks to the JSON structure, no big deal
	 * @augments module:cardboard/collections.Collection
	 */
	CardboardCollections.DemoCatProducer = CardboardCollections.Collection.extend(/** @lends module:cardboard/collections.DemoCatProducer.prototype */ {
		/** override fetch to change the JSON structure */
		fetch: function () {
			var self = this;

/*             if((this.url == "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_maternalTraitsAttendance_nbr2.fex") || (this.url == "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_maternalTraitsProficiency_nbr2.fex") || (this.url == "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_3rdGradeEdStatus_nbr2.fex") || (this.url == "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_birthOutcomes_nbr2.fex") || (this.url == "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_ECSEto3rdGrade_nbr2.fex"))
              {  
              	self.params = null;
              }
			  console.log(self.params); */
               
			return CardboardCollections.Collection.prototype.fetch.apply(this, arguments).then(function (data) {
				self.loaded = true;
				return data;
			});
		},
		
		/** is this a multi-select, or a grouped type?
		 * @returns {Boolean} */
		isMultiSelect: function () {
			return (this.at(1).get("selectType") === "nonGrouped");
		},

		/** what's the max number of selections for this demo cat? 
		 * @returns {Number} */
		getMaxSelections: function () {
			return this.at(1).get("maxSelections");
		},
		
		/** override native toJSON() to produce an appropriate data view */
		toJSON: function () {
			var dataView,
				originalJSON = CardboardCollections.Collection.prototype.toJSON.call(this);

			if (this.isMultiSelect()) {
				dataView = {
					options: originalJSON
				};
			} else {
				dataView = {
					groups: _(originalJSON).groupBy("groupCode").values().value()
				};
			}

			return dataView;
		}
	});
	return CardboardCollections;
});

